/**
 * The manufacturer's binding of the shared {@link TimelineSection}.
 *
 * Everything generic — the list, the event modal, the enrichment dialog, the
 * transactional write discipline — lives in `components/Timeline`. This file is
 * only what is true of manufacturers specifically: which document to write,
 * where the images live, which callable enriches, and the one control the brand
 * side has no equivalent for.
 */

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import type { TimelineEvent, TimelineEventImage } from '@watchkeeper/shared-types';
// The ADMIN's MovementManufacturer, not shared-types' — it is
// `Omit<Shared, 'createdAt'|'updatedAt'>` with Firestore `Timestamp`s, so it
// inherits `history` while staying assignable from what the editor holds.
import type { MovementManufacturer } from '../../types';
import { TimelineSection, type TimelineOwner } from '../Timeline/TimelineSection';
import {
  enrichManufacturerHistoryWithAI,
  updateMovementManufacturer,
  uploadManufacturerHistoryHeroImage,
  uploadManufacturerTimelineImage,
} from '../../services/firebase';

interface Props {
  manufacturer: MovementManufacturer;
  /** Lets the host refresh its copy after a write, as the other sections do. */
  onManufacturerChange?: (next: MovementManufacturer) => void;
}

export function ManufacturerTimelineSection({ manufacturer, onManufacturerChange }: Props) {
  const owner: TimelineOwner = {
    kind: 'manufacturer',
    id: manufacturer.id,
    name: manufacturer.name || manufacturer.id,
    history: manufacturer.history,
    storagePrefix: `manufacturers/${manufacturer.id}/timeline/`,
    persist: async (history: TimelineEvent[]) => {
      await updateMovementManufacturer(manufacturer.id, { history });
      onManufacturerChange?.({ ...manufacturer, history });
    },
    uploadImage: (file, fileName) =>
      uploadManufacturerTimelineImage(manufacturer.id, file, fileName),
    enrich: (input) =>
      enrichManufacturerHistoryWithAI({ manufacturerId: manufacturer.id, ...input }),
    historyHeroImage: manufacturer.historyHeroImage,
    heroStoragePrefix: `manufacturers/${manufacturer.id}/history-hero/`,
    uploadHeroImage: (file, fileName) =>
      uploadManufacturerHistoryHeroImage(manufacturer.id, file, fileName),
    persistHeroImage: async (historyHeroImage: TimelineEventImage | null) => {
      // `null`, never `undefined`. Both updaters run a shallow `stripUndefined`
      // before `updateDoc`, and `updateDoc` merges — so an undefined value here
      // would be dropped from the write and the OLD hero would survive a
      // Remove, looking saved until the page was reopened. `null` survives the
      // strip and erases the field. The updater's parameter type does not admit
      // null, hence the widening cast.
      await updateMovementManufacturer(manufacturer.id, {
        historyHeroImage,
      } as unknown as Partial<Omit<MovementManufacturer, 'id'>>);
      onManufacturerChange?.({
        ...manufacturer,
        historyHeroImage: historyHeroImage ?? undefined,
      });
    },
  };

  return (
    <TimelineSection
      owner={owner}
      headerExtra={
        <HideFromPrimaryBrand
          manufacturer={manufacturer}
          onManufacturerChange={onManufacturerChange}
        />
      }
    />
  );
}

/**
 * "Do not link from primary brand's pages" — one boolean, written on toggle.
 *
 * Manufacturer-only: it governs the manufacturer→primary-brand link, which has
 * no brand-side equivalent. That is why it is passed in as `headerExtra` rather
 * than living in the shared section.
 *
 * Optimistic: the box flips immediately and rolls back if the write throws, so
 * it never claims a state the document does not hold. Written on toggle rather
 * than folded into the manufacturer form's Save because every other control in
 * this section persists on click; a checkbox that needed a DIFFERENT Save would
 * rebuild, in miniature, the two-saves confusion the section was reshaped to
 * remove.
 *
 * `false` is written rather than the key deleted. The type documents
 * absent/undefined as equivalent to false, so an explicit false says the same
 * thing to every reader and avoids a `deleteField()` special case.
 */
function HideFromPrimaryBrand({
  manufacturer,
  onManufacturerChange,
}: {
  manufacturer: MovementManufacturer;
  onManufacturerChange?: (next: MovementManufacturer) => void;
}) {
  const [hide, setHide] = useState(manufacturer.hideTimelineFromPrimaryBrand === true);
  const [saving, setSaving] = useState(false);

  // Re-sync if the host hands down a different manufacturer, or a fresh copy
  // after somebody else wrote the flag.
  useEffect(() => {
    setHide(manufacturer.hideTimelineFromPrimaryBrand === true);
  }, [manufacturer.id, manufacturer.hideTimelineFromPrimaryBrand]);

  const toggle = useCallback(
    async (next: boolean) => {
      const previous = hide;
      setHide(next);
      setSaving(true);
      try {
        await updateMovementManufacturer(manufacturer.id, {
          hideTimelineFromPrimaryBrand: next,
        });
        onManufacturerChange?.({ ...manufacturer, hideTimelineFromPrimaryBrand: next });
      } catch (err) {
        console.error('Failed to save the timeline link setting:', err);
        setHide(previous);
        toast.error('Could not save that setting.');
      } finally {
        setSaving(false);
      }
    },
    [hide, manufacturer, onManufacturerChange],
  );

  return (
    <label className="mt-2 flex items-center gap-2 text-[11px] text-gray-600">
      <input
        type="checkbox"
        checked={hide}
        disabled={saving}
        onChange={(e) => void toggle(e.target.checked)}
      />
      <span>
        Do not link from primary brand&rsquo;s pages
        <span className="ml-1 text-gray-400">
          {manufacturer.primaryBrandId
            ? '(saves immediately)'
            : '— no primary brand set, so nothing links here yet'}
        </span>
      </span>
    </label>
  );
}
